"use client";

import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useState,
} from "react";
import { Check, ChevronDown, Link2 } from "lucide-react";
import { AeraButton, ModalCloseButton, ModalShell } from "@aera/ui";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/app/components/ui/DropdownMenu";

const RECORD_DRAWER_PANEL_SELECTOR =
  "[data-aera-modal-panel].record-drawer-modal-panel";

import {
  formatReceivableExpiryRemaining,
  getReceivableExpiresAtMs,
  getReceivableOrderId,
  isReceivableAnnouncementItem,
  matchPaymentToReceivables,
  matchReceivablePayment,
  type PaymentAllocationInput,
  type ReceivableListItem,
} from "@/lib/pendingOrders";
import { offerMoneyUndoToast, pickMoneyUndoSourceLabel, formatAllocateUndoLabel } from "@/lib/moneyUndoToast";
import { pickOperationId } from "@/lib/moneyOperations";
import { showToast } from "@/app/components/ui/Toast";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/app/components/ui/Tooltip";
import { formatThaiDatetime } from "@aera/data-table";
import { isReceivableUsableMoneyCandidate } from "@/lib/receivableDisplayStatus";

export type ReceivableMatchModalMode = "from-receivable" | "from-slip";

function pickAmountLabel(item: ReceivableListItem): string {
  if (item.amountLabel?.trim()) return item.amountLabel.trim();
  const field = item.fields.find((f) =>
    ["amount", "slip_amount", "transfer_amount"].includes(f.key),
  );
  return field?.value?.trim() || "—";
}

function pickTitle(item: ReceivableListItem): string {
  const customer = item.fields.find((f) => f.key === "customer_ref")?.value;
  if (customer?.trim()) return customer.trim();
  const sender = item.fields.find((f) => f.key === "slip_sender")?.value;
  if (sender?.trim()) return sender.trim();
  return item.title?.trim() || "—";
}

function pickExpiresAt(item: ReceivableListItem): string | null {
  const value = item.fields.find((f) => f.key === "expires_at")?.value?.trim();
  return value && value !== "—" ? value : null;
}

function MatchListExpiryLabel({ value }: { value: string }) {
  const [, setTick] = useState(0);

  useEffect(() => {
    const id = window.setInterval(() => setTick((tick) => tick + 1), 60_000);
    return () => window.clearInterval(id);
  }, []);

  const label = formatReceivableExpiryRemaining(value);
  const isExpired = label === "หมดอายุ";
  const expiresMs = Date.parse(value);
  const isUrgent =
    Number.isFinite(expiresMs) &&
    expiresMs > Date.now() &&
    expiresMs - Date.now() < 3_600_000;
  const valueColorClass = isExpired || isUrgent ? "text-aera-red" : "text-aera-gray";

  return (
    <span className="shrink-0 text-right text-xs font-medium leading-5 whitespace-nowrap">
      {isExpired ? (
        <span className="text-aera-red">{label}</span>
      ) : (
        <>
          <span className="text-aera-gray">หมดอายุใน </span>
          <span className={valueColorClass}>{label}</span>
        </>
      )}
    </span>
  );
}

function formatSubtitleDatetime(value: string | null | undefined): string | null {
  const trimmed = value?.trim();
  if (!trimmed || trimmed === "—") return null;
  // Already localized (e.g. 11/ก.ค./69 (13:35)) — keep as-is.
  if (!/^\d{4}-\d{2}-\d{2}/.test(trimmed)) return trimmed;
  const formatted = formatThaiDatetime(trimmed);
  return formatted && formatted !== "—" ? formatted : trimmed;
}

function pickSubtitle(item: ReceivableListItem): string {
  const bank = item.fields.find((f) => f.key === "bank")?.value?.trim();
  const transfer = formatSubtitleDatetime(
    item.fields.find((f) => f.key === "transfer_date" || f.key === "transfer_datetime")
      ?.value ?? item.transferDate,
  );
  const created = formatSubtitleDatetime(
    item.fields.find((f) => f.key === "created_at")?.value ?? item.createdAt,
  );
  const when = transfer || created;
  const parts = [bank, when].filter((v): v is string => Boolean(v?.trim()));
  return parts.join(" · ") || item.detail?.trim() || "";
}

function parseAmountNumber(label: string): number | null {
  const normalized = label.replace(/[^\d.,-]/g, "").replace(/,/g, "");
  if (!normalized) return null;
  const value = Number.parseFloat(normalized);
  return Number.isFinite(value) ? value : null;
}

type MatchListSort =
  | "expiry"
  | "created_latest"
  | "name"
  | "amount_asc"
  | "amount_desc";

const MATCH_LIST_SORT_OPTIONS: { id: MatchListSort; label: string }[] = [
  { id: "expiry", label: "ใกล้หมดอายุ" },
  { id: "created_latest", label: "วันที่เพิ่มล่าสุด" },
  { id: "name", label: "ชื่อ" },
  { id: "amount_asc", label: "ยอดต่ำสุด" },
  { id: "amount_desc", label: "ยอดสูงสุด" },
];

function pickCreatedAtMs(item: ReceivableListItem): number | null {
  const raw =
    item.createdAt?.trim() ||
    item.fields.find((f) => f.key === "created_at")?.value?.trim() ||
    "";
  if (!raw || raw === "—") return null;
  const ms = Date.parse(raw);
  return Number.isFinite(ms) ? ms : null;
}

function pickSortAmount(item: ReceivableListItem): number {
  const remaining = item.fields.find((f) => f.key === "amount_remaining")?.value;
  if (remaining) {
    const parsed = parseAmountNumber(remaining);
    if (parsed !== null) return parsed;
  }
  return parseAmountNumber(pickAmountLabel(item)) ?? 0;
}

function compareNullableMs(
  leftMs: number | null,
  rightMs: number | null,
  dir: "asc" | "desc",
): number {
  if (leftMs === null && rightMs === null) return 0;
  if (leftMs === null) return 1;
  if (rightMs === null) return -1;
  return dir === "asc" ? leftMs - rightMs : rightMs - leftMs;
}

function sortMatchCounterparts(
  items: ReceivableListItem[],
  sortBy: MatchListSort,
): ReceivableListItem[] {
  return [...items].sort((left, right) => {
    if (sortBy === "expiry") {
      return compareNullableMs(
        getReceivableExpiresAtMs(left),
        getReceivableExpiresAtMs(right),
        "asc",
      );
    }
    if (sortBy === "created_latest") {
      return compareNullableMs(
        pickCreatedAtMs(left),
        pickCreatedAtMs(right),
        "desc",
      );
    }
    if (sortBy === "name") {
      return pickTitle(left).localeCompare(pickTitle(right), "th");
    }
    if (sortBy === "amount_asc") {
      return pickSortAmount(left) - pickSortAmount(right);
    }
    return pickSortAmount(right) - pickSortAmount(left);
  });
}

function formatAmount(value: number): string {
  return value.toLocaleString("th-TH", {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  });
}

type SelectedEntry = {
  item: ReceivableListItem;
  amount: string;
};

function pickSlipRemainingAmount(item: ReceivableListItem): number | null {
  const remainingField = item.fields.find((f) => f.key === "amount_remaining");
  if (remainingField?.value) {
    const parsed = parseAmountNumber(remainingField.value);
    if (parsed !== null) return parsed;
  }
  return parseAmountNumber(pickAmountLabel(item));
}

function pickSlipOriginalAmount(item: ReceivableListItem): number | null {
  const originalField = item.fields.find((f) => f.key === "amount_original");
  if (originalField?.value) {
    const parsed = parseAmountNumber(originalField.value);
    if (parsed !== null) return parsed;
  }
  return pickSlipRemainingAmount(item);
}

function pickReceivableRemainingAmount(item: ReceivableListItem): number | null {
  const remainingField = item.fields.find((f) => f.key === "amount_remaining");
  if (remainingField?.value) {
    const parsed = parseAmountNumber(remainingField.value);
    if (parsed !== null) return parsed;
  }
  return parseAmountNumber(pickAmountLabel(item));
}

function validateFromReceivable(
  entries: SelectedEntry[],
  receivableAmount: number | null,
): string | null {
  if (entries.length === 0) {
    return "กรุณาเลือกสลิปอย่างน้อย 1 รายการ";
  }

  let total = 0;
  for (const entry of entries) {
    const slipRemaining = pickSlipRemainingAmount(entry.item);
    const requested = parseAmountNumber(entry.amount);

    if (requested === null || requested <= 0) {
      return "กรุณาระบุยอดที่เลือกมาจับคู่ต้องระบุให้มากกว่า 0";
    }
    if (slipRemaining !== null && requested > slipRemaining + 0.0001) {
      return `ยอด ${formatAmount(requested)} บาท เกินยอดสลิปที่เหลือ ${formatAmount(slipRemaining)} บาท`;
    }
    total += requested;
  }

  if (receivableAmount !== null && total > receivableAmount + 0.0001) {
    return `ยอดรวม ${formatAmount(total)} บาท เกินยอดค้าง ${formatAmount(receivableAmount)} บาท`;
  }

  return null;
}

function validateFromSlip(
  entries: SelectedEntry[],
  slipRemaining: number | null,
): string | null {
  const active = activeAllocationEntries(entries);
  if (active.length === 0) {
    return "กรุณาระบุยอดที่ต้องการตัดให้มากกว่า 0";
  }

  let total = 0;
  for (const entry of active) {
    const receivableRemaining = pickReceivableRemainingAmount(entry.item);
    const requested = parseAmountNumber(entry.amount);

    if (requested === null || requested <= 0) {
      continue;
    }
    if (
      receivableRemaining !== null &&
      requested > receivableRemaining + 0.0001
    ) {
      return `ยอด ${formatAmount(requested)} บาท เกินยอดค้าง ${formatAmount(receivableRemaining)} บาท (${pickTitle(entry.item)})`;
    }
    total += requested;
  }

  if (slipRemaining !== null && total > slipRemaining + 0.0001) {
    return `ยอดรวม ${formatAmount(total)} บาท เกินยอดสลิปที่เหลือ ${formatAmount(slipRemaining)} บาท`;
  }

  return null;
}

function allocateBudgetAcrossEntries(
  entries: SelectedEntry[],
  budget: number | null,
  pickRemaining: (item: ReceivableListItem) => number | null,
): SelectedEntry[] {
  if (entries.length === 0) return entries;

  let left = budget ?? Number.POSITIVE_INFINITY;
  return entries.map((entry) => {
    const counterpartRemaining = pickRemaining(entry.item);
    const cap =
      counterpartRemaining !== null
        ? Math.min(counterpartRemaining, left)
        : left;
    const amount = Math.max(0, cap);
    if (Number.isFinite(left)) {
      left -= amount;
    }
    return { ...entry, amount: String(amount) };
  });
}

function activeAllocationEntries(entries: SelectedEntry[]): SelectedEntry[] {
  return entries.filter((entry) => (parseAmountNumber(entry.amount) ?? 0) > 0);
}

type Step = "pick" | "confirm";

export function ReceivableMatchPaymentModal({
  open,
  onOpenChange,
  mode = "from-receivable",
  anchorItem,
  counterpartItems,
  ownerUserId,
  workspaceId = null,
  onMatched,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  mode?: ReceivableMatchModalMode;
  anchorItem: ReceivableListItem | null;
  counterpartItems: ReceivableListItem[];
  ownerUserId: string | null;
  workspaceId?: string | null;
  onMatched?: () => void | Promise<void>;
}) {
  const [step, setStep] = useState<Step>("pick");
  const [selectedEntries, setSelectedEntries] = useState<SelectedEntry[]>([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [listSort, setListSort] = useState<MatchListSort>("expiry");
  const [sortMenuOpen, setSortMenuOpen] = useState(false);
  const [scopedHost, setScopedHost] = useState<Element | null>(null);

  useLayoutEffect(() => {
    if (!open) return;
    const host =
      document.querySelector(RECORD_DRAWER_PANEL_SELECTOR) ??
      document.querySelector("[data-aera-modal-panel]");
    setScopedHost(host);
  }, [open]);

  const isScoped = scopedHost != null;
  const fromReceivable = mode === "from-receivable";

  const availableCounterparts = useMemo(() => {
    const items = fromReceivable
      ? counterpartItems.filter((item) => !isReceivableAnnouncementItem(item))
      : counterpartItems.filter((item) => isReceivableAnnouncementItem(item));
    return sortMatchCounterparts(
      items.filter(isReceivableUsableMoneyCandidate),
      listSort,
    );
  }, [counterpartItems, fromReceivable, listSort]);

  const anchorReceivableAmount = useMemo(
    () =>
      fromReceivable && anchorItem
        ? parseAmountNumber(pickAmountLabel(anchorItem))
        : null,
    [anchorItem, fromReceivable],
  );

  const anchorSlipRemaining = useMemo(
    () =>
      !fromReceivable && anchorItem
        ? pickSlipRemainingAmount(anchorItem)
        : null,
    [anchorItem, fromReceivable],
  );

  const selectedTotal = useMemo(
    () =>
      selectedEntries.reduce(
        (sum, entry) => sum + (parseAmountNumber(entry.amount) ?? 0),
        0,
      ),
    [selectedEntries],
  );

  const allocationValidationError = useMemo(() => {
    if (step !== "confirm") return null;
    if (fromReceivable) {
      return validateFromReceivable(selectedEntries, anchorReceivableAmount);
    }
    return validateFromSlip(selectedEntries, anchorSlipRemaining);
  }, [
    anchorReceivableAmount,
    anchorSlipRemaining,
    fromReceivable,
    selectedEntries,
    step,
  ]);

  const reset = useCallback(() => {
    setStep("pick");
    setSelectedEntries([]);
    setIsSubmitting(false);
    setError(null);
    setListSort("expiry");
    setSortMenuOpen(false);
  }, []);

  const handleClose = useCallback(() => {
    onOpenChange(false);
  }, [onOpenChange]);

  const handleExited = useCallback(() => {
    reset();
  }, [reset]);

  const defaultAmountForItem = useCallback(
    (item: ReceivableListItem): string => {
      if (fromReceivable) {
        const amount = pickSlipRemainingAmount(item);
        return amount !== null ? String(amount) : pickAmountLabel(item);
      }
      const receivableRemaining = pickReceivableRemainingAmount(item);
      return receivableRemaining !== null
        ? String(receivableRemaining)
        : pickAmountLabel(item);
    },
    [fromReceivable],
  );

  const goToConfirmStep = useCallback(() => {
    setSelectedEntries((current) => {
      if (fromReceivable) {
        return allocateBudgetAcrossEntries(
          current,
          anchorReceivableAmount,
          pickSlipRemainingAmount,
        );
      }
      return allocateBudgetAcrossEntries(
        current,
        anchorSlipRemaining,
        pickReceivableRemainingAmount,
      );
    });
    setStep("confirm");
    setError(null);
  }, [anchorReceivableAmount, anchorSlipRemaining, fromReceivable]);

  const toggleEntry = useCallback(
    (item: ReceivableListItem) => {
      setSelectedEntries((current) => {
        const exists = current.some((entry) => entry.item.mergeKey === item.mergeKey);
        if (exists) {
          return current.filter((entry) => entry.item.mergeKey !== item.mergeKey);
        }
        return [
          ...current,
          { item, amount: defaultAmountForItem(item) },
        ];
      });
      setError(null);
    },
    [defaultAmountForItem],
  );

  const commitAmount = useCallback(
    (mergeKey: string, amount: string) => {
      setSelectedEntries((current) =>
        current.map((entry) => {
          if (entry.item.mergeKey !== mergeKey) return entry;
          const requested = parseAmountNumber(amount);
          if (requested === null) return { ...entry, amount };

          let capped = requested;
          if (fromReceivable) {
            const slipRemaining = pickSlipRemainingAmount(entry.item);
            if (slipRemaining !== null && capped > slipRemaining) {
              capped = slipRemaining;
            }
          } else {
            const receivableRemaining = pickReceivableRemainingAmount(entry.item);
            if (receivableRemaining !== null && capped > receivableRemaining) {
              capped = receivableRemaining;
            }
          }
          return { ...entry, amount: String(capped) };
        }),
      );
      setError(null);
    },
    [fromReceivable],
  );

  const updateAmount = useCallback((mergeKey: string, amount: string) => {
    setSelectedEntries((current) =>
      current.map((entry) =>
        entry.item.mergeKey === mergeKey ? { ...entry, amount } : entry,
      ),
    );
    setError(null);
  }, []);

  const poolBudget = fromReceivable
    ? anchorReceivableAmount
    : anchorSlipRemaining;

  const computeMaxAmount = useCallback(
    (entry: SelectedEntry, leftoverFromPool: number): number => {
      const current = parseAmountNumber(entry.amount) ?? 0;
      const itemCap = fromReceivable
        ? pickSlipRemainingAmount(entry.item)
        : pickReceivableRemainingAmount(entry.item);
      const poolMax =
        poolBudget === null
          ? itemCap ?? current
          : current + Math.max(0, leftoverFromPool);
      if (itemCap === null) return Math.max(0, poolMax);
      return Math.max(0, Math.min(poolMax, itemCap));
    },
    [fromReceivable, poolBudget],
  );

  const fillMaxAmount = useCallback(
    (mergeKey: string) => {
      setSelectedEntries((current) => {
        const draftSum = current.reduce(
          (sum, entry) => sum + (parseAmountNumber(entry.amount) ?? 0),
          0,
        );
        const leftover =
          poolBudget === null ? Number.POSITIVE_INFINITY : poolBudget - draftSum;
        return current.map((entry) => {
          if (entry.item.mergeKey !== mergeKey) return entry;
          return {
            ...entry,
            amount: String(computeMaxAmount(entry, leftover)),
          };
        });
      });
      setError(null);
    },
    [computeMaxAmount, poolBudget],
  );

  const handleConfirm = useCallback(async () => {
    if (!anchorItem || selectedEntries.length === 0 || !ownerUserId?.trim()) {
      return;
    }

    const validationError = fromReceivable
      ? validateFromReceivable(selectedEntries, anchorReceivableAmount)
      : validateFromSlip(selectedEntries, anchorSlipRemaining);
    if (validationError) {
      setError(validationError);
      return;
    }

    const entriesToApply = fromReceivable
      ? selectedEntries
      : activeAllocationEntries(selectedEntries);
    if (entriesToApply.length === 0) {
      setError("กรุณาระบุยอดที่ต้องการตัดให้มากกว่า 0");
      return;
    }

    setIsSubmitting(true);
    setError(null);
    try {
      // Always go through orders/match → _do_confirm → record_persist.
      // PG-only /corrections/allocate updates money but does NOT create รายรับ.
      const ws = workspaceId?.trim() || "";
      let lastOperationId: string | null = null;

      if (fromReceivable) {
        const receivableOrderId = getReceivableOrderId(anchorItem);
        if (!receivableOrderId) {
          setError("ไม่พบรหัสยอดค้าง กรุณารีเฟรชแล้วลองใหม่");
          return;
        }
        const allocations: PaymentAllocationInput[] = [];
        for (const entry of entriesToApply) {
          const paymentOrderId = getReceivableOrderId(entry.item);
          if (!paymentOrderId) {
            setError("ไม่พบรหัสสลิป กรุณารีเฟรชแล้วลองใหม่");
            return;
          }
          const requested = parseAmountNumber(entry.amount);
          allocations.push({
            payment_order_id: paymentOrderId,
            amount:
              requested !== null && requested > 0
                ? String(requested)
                : entry.amount.trim(),
          });
        }
        const result = await matchReceivablePayment(
          ownerUserId.trim(),
          receivableOrderId,
          allocations,
        );
        lastOperationId =
          (typeof result.operation_id === "string" && result.operation_id) ||
          pickOperationId({ after: result.after ?? undefined }) ||
          null;
      } else {
        const paymentOrderId = getReceivableOrderId(anchorItem);
        if (!paymentOrderId) {
          setError("ไม่พบรหัสสลิป กรุณารีเฟรชแล้วลองใหม่");
          return;
        }
        const receivableAllocations: Array<{
          receivable_order_id: string;
          amount: string;
        }> = [];
        for (const entry of entriesToApply) {
          const receivableOrderId = getReceivableOrderId(entry.item);
          if (!receivableOrderId) {
            setError("ไม่พบรหัสยอดค้าง กรุณารีเฟรชแล้วลองใหม่");
            return;
          }
          const requested = parseAmountNumber(entry.amount);
          receivableAllocations.push({
            receivable_order_id: receivableOrderId,
            amount:
              requested !== null && requested > 0
                ? String(requested)
                : entry.amount.trim(),
          });
        }
        // One atomic allocate_many so Undo restores every ยอดค้าง together.
        const result = await matchPaymentToReceivables(
          ownerUserId.trim(),
          paymentOrderId,
          receivableAllocations,
        );
        lastOperationId =
          (typeof result.operation_id === "string" && result.operation_id) ||
          pickOperationId({ after: result.after ?? undefined }) ||
          null;
      }

      if (ownerUserId?.trim() && ws) {
        // History-style with both sides:
        // from-slip:      จับคู่ยอดค้าง 10 บาทจาก ผู้โอนสลิป → พี่เขียว 10
        // from-receivable: จับคู่ยอดค้าง 10 บาทจาก ผู้โอนสลิป 10 → พี่เขียว
        const parts = entriesToApply.map((entry) => ({
          label:
            pickMoneyUndoSourceLabel(entry.item) ||
            pickTitle(entry.item) ||
            "ลูกค้า",
          amount: parseAmountNumber(entry.amount) ?? 0,
        }));
        const anchorLabel =
          pickMoneyUndoSourceLabel(anchorItem) ||
          pickTitle(anchorItem) ||
          "";
        const label = fromReceivable
          ? formatAllocateUndoLabel(
              parts,
              "จับคู่ยอดค้าง",
              "จับคู่สำเร็จ",
              { toLabel: anchorLabel || undefined },
            )
          : formatAllocateUndoLabel(
              parts,
              "จับคู่ยอดค้าง",
              "ตัดยอดค้างสำเร็จ",
              { fromLabel: anchorLabel || undefined },
            );
        offerMoneyUndoToast({
          message: label,
          ownerUserId: ownerUserId.trim(),
          workspaceId: ws,
          operationId: lastOperationId,
          onUndone: onMatched,
        });
      } else {
        showToast(fromReceivable ? "จับคู่สำเร็จ" : "ตัดยอดค้างสำเร็จ");
      }
      await onMatched?.();
      handleClose();
    } catch (err) {
      const message =
        err instanceof Error ? err.message : "จับคู่ไม่สำเร็จ กรุณาลองใหม่";
      setError(message);
    } finally {
      setIsSubmitting(false);
    }
  }, [
    anchorItem,
    anchorReceivableAmount,
    anchorSlipRemaining,
    fromReceivable,
    handleClose,
    onMatched,
    ownerUserId,
    selectedEntries,
    workspaceId,
  ]);

  if (!anchorItem) return null;

  const modalTitle = fromReceivable ? "จับคู่การชำระเงิน" : "ตัดยอดค้าง";
  const pickHint = fromReceivable
    ? "เลือกสลิปจากรายการสลิปค้าง (เลือกได้หลายรายการ) แล้วกำหนดยอดในขั้นตอนถัดไป"
    : "เลือกยอดค้างที่ต้องการตัด (เลือกได้หลายรายการ) แล้วกำหนดยอดในขั้นตอนถัดไป";
  const emptyHint = fromReceivable
    ? "ไม่มีสลิปค้างให้จับคู่"
    : "ไม่มียอดค้างให้ตัด";
  const counterpartUnit = fromReceivable ? "สลิป" : "ยอดค้าง";
  const confirmStepLabel = fromReceivable
    ? "กำหนดยอดที่ต้องการจับคู่ต่อสลิป"
    : "กำหนดยอดที่ต้องการตัดต่อยอดค้าง";

  const remaining = !fromReceivable
    ? pickSlipRemainingAmount(anchorItem)
    : parseAmountNumber(pickAmountLabel(anchorItem));
  const summaryLine = (
    <>
      <span className="font-semibold text-aera-charcoal">
        {pickTitle(anchorItem)}
      </span>
      {remaining !== null ? (
        <>
          {" · "}
          <span className="tabular-nums">
            {fromReceivable ? "" : "เหลือ "}฿{formatAmount(remaining)}
          </span>
        </>
      ) : null}
      {pickSubtitle(anchorItem) ? (
        <>
          {" · "}
          {pickSubtitle(anchorItem)}
        </>
      ) : null}
    </>
  );

  const close = () => {
    if (isSubmitting) return;
    handleClose();
  };

  const slipRemainingAfter =
    !fromReceivable && anchorSlipRemaining !== null
      ? Math.max(0, anchorSlipRemaining - selectedTotal)
      : null;

  const slipMatchSummary =
    !fromReceivable && anchorSlipRemaining !== null ? (
      <div className="flex items-end justify-between gap-3">
        <div className="min-w-0">
          <p className="text-xs font-medium text-aera-gray">สลิปนี้</p>
          <p className="text-sm font-semibold tabular-nums text-aera-charcoal">
            ฿{formatAmount(anchorSlipRemaining)}
          </p>
        </div>
        {selectedEntries.length > 0 ? (
          <div className="shrink-0 text-right">
            <p className="text-xs font-medium text-aera-gray">
              เลือกจับคู่{" "}
              <span className="font-semibold tabular-nums text-aera-amberdark">
                ฿{formatAmount(selectedTotal)}
              </span>
            </p>
            <p
              className={`text-sm font-semibold tabular-nums ${
                slipRemainingAfter !== null && slipRemainingAfter > 0
                  ? "text-aera-green"
                  : "text-aera-charcoal"
              }`}
            >
              {slipRemainingAfter !== null && slipRemainingAfter > 0
                ? `จะเหลือ ฿${formatAmount(slipRemainingAfter)}`
                : "ใช้ยอดสลิปพอดี"}
            </p>
          </div>
        ) : (
          <p className="max-w-[55%] shrink-0 text-right text-xs leading-snug text-aera-gray">
            เลือกยอดค้างเพื่อดูยอดที่จะเหลือ
          </p>
        )}
      </div>
    ) : null;

  return (
    <ModalShell
      isOpen={open}
      onClose={close}
      onExited={() => {
        setScopedHost(null);
        handleExited();
      }}
      align="bottom"
      scopedHost={isScoped ? scopedHost : null}
      preventBackdropClose={isSubmitting}
      zIndexClass="z-[10050]"
      overlayClassName={
        isScoped ? "bg-aera-black/40 rounded-[inherit]" : undefined
      }
      panelClassName={[
        isScoped
          ? "flex flex-col overflow-hidden rounded-t-3xl rounded-b-[inherit] border-t border-aera-lightgray bg-aera-white shadow-xl"
          : "flex flex-col overflow-hidden rounded-t-3xl rounded-b-none border-t border-aera-lightgray bg-aera-white shadow-xl",
        isScoped ? "max-h-[85%]" : "max-h-[80dvh]",
      ].join(" ")}
    >
      <div className="flex shrink-0 justify-center pb-1 pt-2">
        <div className="h-1 w-10 rounded-full bg-aera-lightgray" aria-hidden />
      </div>

      <header className="flex shrink-0 flex-col px-4 pb-4">
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <h2 className="text-xl font-bold text-aera-charcoal">
              <span className="flex items-center gap-2 text-aera-green">
                <Link2 className="h-5 w-5 shrink-0" strokeWidth={2} aria-hidden />
                {modalTitle}
              </span>
            </h2>
            <p className="mt-1 text-sm leading-relaxed text-aera-gray">
              {summaryLine}
            </p>
            {step === "pick" ? (
              <p className="mt-1 text-sm leading-relaxed text-aera-gray">
                {pickHint}
              </p>
            ) : (
              <p className="mt-1 text-sm leading-relaxed text-aera-gray">
                {confirmStepLabel}
              </p>
            )}
          </div>
          <ModalCloseButton
            onClose={close}
            disabled={isSubmitting}
            className="-mr-1 shrink-0"
          />
        </div>
        {step === "pick" && availableCounterparts.length > 0 ? (
          <div className="mt-1.5 flex w-full items-center justify-between gap-3">
            {selectedEntries.length > 0 ? (
              <p className="min-w-0 text-sm font-semibold text-aera-green">
                เลือกแล้ว {selectedEntries.length} {counterpartUnit}
              </p>
            ) : (
              <span className="min-w-0" aria-hidden />
            )}
            <div className="ms-auto flex shrink-0 items-center gap-2">
              <span className="text-xs font-medium text-aera-gray">เรียงตาม</span>
              <DropdownMenu open={sortMenuOpen} onOpenChange={setSortMenuOpen}>
                <DropdownMenuTrigger asChild>
                  <AeraButton
                    type="button"
                    variant="outline"
                    size="xs"
                    fontWeight="semibold"
                    className="min-w-0"
                    aria-label={`เรียงตาม: ${
                      MATCH_LIST_SORT_OPTIONS.find((o) => o.id === listSort)
                        ?.label ?? ""
                    }`}
                    aria-expanded={sortMenuOpen}
                    iconRight={
                      <ChevronDown
                        size={14}
                        className={`text-aera-gray transition-transform duration-200 ${
                          sortMenuOpen ? "rotate-180" : "rotate-0"
                        }`}
                      />
                    }
                  >
                    {MATCH_LIST_SORT_OPTIONS.find((o) => o.id === listSort)
                      ?.label ?? "ใกล้หมดอายุ"}
                  </AeraButton>
                </DropdownMenuTrigger>
                <DropdownMenuContent
                  align="end"
                  sideOffset={6}
                  className="z-[10100] min-w-[11.5rem] rounded-xl border border-aera-lightgray/80 p-1.5 shadow-[0_10px_30px_rgba(0,0,0,0.08)]"
                >
                  {MATCH_LIST_SORT_OPTIONS.map((option) => {
                    const active = listSort === option.id;
                    return (
                      <DropdownMenuItem
                        key={option.id}
                        onClick={() => {
                          setListSort(option.id);
                          setSortMenuOpen(false);
                        }}
                        className="cursor-pointer justify-between gap-3 rounded-lg px-2.5 py-2 text-xs font-semibold text-aera-charcoal"
                      >
                        <span>{option.label}</span>
                        {active ? (
                          <Check
                            className="h-3.5 w-3.5 shrink-0 text-aera-amber"
                            strokeWidth={3}
                            aria-hidden
                          />
                        ) : (
                          <span className="h-3.5 w-3.5 shrink-0" aria-hidden />
                        )}
                      </DropdownMenuItem>
                    );
                  })}
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          </div>
        ) : null}
      </header>

      <div className="min-h-0 flex-1 overflow-y-auto px-4 py-3 text-aera-charcoal">
        {error ? (
          <p className="mb-3 rounded-lg bg-aera-redlight px-3 py-2 text-sm font-medium text-aera-red">
            {error}
          </p>
        ) : null}

        {step === "pick" ? (
          <div className="space-y-3">
            {availableCounterparts.length === 0 ? (
              <p className="rounded-xl border border-dashed border-aera-lightgray px-4 py-8 text-center text-sm text-aera-gray">
                {emptyHint}
              </p>
            ) : (
              <ul className="space-y-2">
                {availableCounterparts.map((item) => {
                  const isSelected = selectedEntries.some(
                    (entry) => entry.item.mergeKey === item.mergeKey,
                  );
                  const expiresAt = pickExpiresAt(item);
                  const listAmount = fromReceivable
                    ? (() => {
                        const slipRemaining = pickSlipRemainingAmount(item);
                        const original = pickSlipOriginalAmount(item);
                        if (
                          slipRemaining !== null &&
                          original !== null &&
                          original > slipRemaining
                        ) {
                          return `เหลือ ${formatAmount(slipRemaining)} บาท (สลิป ${formatAmount(original)})`;
                        }
                        return pickAmountLabel(item);
                      })()
                    : pickAmountLabel(item);
                  return (
                    <li key={item.mergeKey}>
                      <button
                        type="button"
                        onClick={() => toggleEntry(item)}
                        className={`flex w-full cursor-pointer items-start justify-between gap-3 rounded-xl border px-4 py-3 text-left transition-colors ${
                          isSelected
                            ? "border-aera-green bg-aera-greenlight/40"
                            : "border-aera-lightgray bg-aera-white hover:border-aera-green/40 hover:bg-aera-greenlight/30"
                        }`}
                      >
                        <div className="flex min-w-0 items-start gap-3">
                          <span
                            className={`mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-md border ${
                              isSelected
                                ? "border-aera-green bg-aera-green text-white"
                                : "border-aera-lightgray bg-white"
                            }`}
                            aria-hidden
                          >
                            {isSelected ? (
                              <Check className="h-3.5 w-3.5" />
                            ) : null}
                          </span>
                          <div className="min-w-0">
                            <p className="font-semibold">{listAmount}</p>
                            <p className="truncate text-sm text-aera-lightslate">
                              {pickTitle(item)}
                            </p>
                            {pickSubtitle(item) ? (
                              <p className="truncate text-xs text-aera-gray">
                                {pickSubtitle(item)}
                              </p>
                            ) : null}
                          </div>
                        </div>
                        {expiresAt ? (
                          <MatchListExpiryLabel value={expiresAt} />
                        ) : null}
                      </button>
                    </li>
                  );
                })}
              </ul>
            )}
          </div>
        ) : (
          <div className="space-y-3">
            {allocationValidationError ? (
              <p className="rounded-lg bg-aera-redlight px-3 py-2 text-sm font-medium text-aera-red">
                {allocationValidationError}
              </p>
            ) : null}
            <TooltipProvider delayDuration={200}>
              <ul className="space-y-2">
                {selectedEntries.map((entry) => {
                  const itemCap = fromReceivable
                    ? pickSlipRemainingAmount(entry.item)
                    : pickReceivableRemainingAmount(entry.item);
                  const entryAmount = parseAmountNumber(entry.amount) ?? 0;
                  const leftover =
                    poolBudget === null
                      ? Number.POSITIVE_INFINITY
                      : poolBudget - selectedTotal;
                  const maxAmt = computeMaxAmount(entry, leftover);
                  const noRoom = maxAmt <= 0.009;
                  const alreadyMax = Math.abs(entryAmount - maxAmt) < 0.005;
                  const maxDisabled = isSubmitting || noRoom || alreadyMax;
                  const maxDisabledReason = isSubmitting
                    ? "กำลังทำงานอยู่ กรุณารอสักครู่"
                    : alreadyMax
                      ? itemCap !== null &&
                        Math.abs(maxAmt - itemCap) < 0.005
                        ? fromReceivable
                          ? `ยอดเต็มตามยอดสลิปแล้ว (฿${formatAmount(maxAmt)})`
                          : `ยอดเต็มตามยอดค้างแล้ว (฿${formatAmount(maxAmt)})`
                        : `ยอดเต็มตามยอดที่จัดสรรได้แล้ว (฿${formatAmount(maxAmt)})`
                      : noRoom
                        ? fromReceivable
                          ? "ไม่มียอดค้างเหลือให้จับคู่เพิ่ม"
                          : "ไม่มียอดสลิปเหลือให้ตัดเพิ่ม"
                        : undefined;
                  const skippedByBudget =
                    !fromReceivable &&
                    entryAmount <= 0 &&
                    selectedEntries.length > 1;

                  const maxButton = (
                    <button
                      type="button"
                      disabled={maxDisabled}
                      onClick={() => fillMaxAmount(entry.item.mergeKey)}
                      className={`rounded-md px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-wide opacity-100 transition-colors ${
                        maxDisabled
                          ? "cursor-not-allowed text-aera-gray pointer-events-none"
                          : "cursor-pointer text-aera-amberdark hover:bg-aera-amber/15"
                      }`}
                      aria-label={
                        maxDisabledReason
                          ? `MAX — ${maxDisabledReason}`
                          : `ใส่ยอดสูงสุดของ ${pickTitle(entry.item)}`
                      }
                    >
                      MAX
                    </button>
                  );

                  return (
                    <li
                      key={entry.item.mergeKey}
                      className={`rounded-xl border px-4 py-3 ${
                        skippedByBudget
                          ? "border-aera-lightgray/80 bg-aera-lightgray/20"
                          : "border-aera-lightgray bg-aera-greenlight/20"
                      }`}
                    >
                      <div className="flex items-start justify-between gap-3">
                        <div className="min-w-0">
                          <p className="truncate text-sm font-semibold text-aera-charcoal">
                            {pickTitle(entry.item)}
                          </p>
                          <p className="mt-0.5 text-xs tabular-nums text-aera-gray">
                            {fromReceivable
                              ? `เหลือ ฿${formatAmount(itemCap ?? 0)}`
                              : `ค้าง ${pickAmountLabel(entry.item)}`}
                          </p>
                          {skippedByBudget ? (
                            <p className="mt-1 text-xs font-medium text-aera-gray">
                              สลิปไม่พอ — ไม่ตัดรายการนี้
                            </p>
                          ) : null}
                        </div>
                        <div className="shrink-0">
                          <label className="block text-xs font-semibold text-aera-gray">
                            {fromReceivable ? "ยอดจับคู่" : "ยอดตัด"}
                          </label>
                          <div className="mt-1 flex items-center gap-1 rounded-lg border border-aera-lightgray bg-white px-2 py-1.5 focus-within:border-aera-amber">
                            <span className="text-sm font-semibold text-aera-gray">
                              ฿
                            </span>
                            <input
                              type="number"
                              min={0}
                              step="1"
                              max={itemCap ?? undefined}
                              value={entry.amount}
                              disabled={isSubmitting}
                              onChange={(event) =>
                                updateAmount(
                                  entry.item.mergeKey,
                                  event.target.value,
                                )
                              }
                              onBlur={(event) =>
                                commitAmount(
                                  entry.item.mergeKey,
                                  event.target.value,
                                )
                              }
                              className="w-20 bg-transparent text-right text-sm font-semibold tabular-nums text-aera-charcoal outline-none disabled:opacity-60"
                            />
                            {maxDisabledReason ? (
                              <Tooltip delayDuration={0}>
                                <TooltipTrigger asChild>
                                  <span className="inline-flex shrink-0 cursor-not-allowed">
                                    {maxButton}
                                  </span>
                                </TooltipTrigger>
                                <TooltipContent
                                  side="top"
                                  sideOffset={6}
                                  collisionPadding={12}
                                  className="z-[10100] w-max max-w-[14rem] whitespace-normal wrap-break-word bg-aera-white text-left leading-relaxed"
                                >
                                  <p>{maxDisabledReason}</p>
                                </TooltipContent>
                              </Tooltip>
                            ) : (
                              <span className="inline-flex shrink-0">
                                {maxButton}
                              </span>
                            )}
                          </div>
                          {itemCap !== null ? (
                            <p className="mt-1 text-right text-xs text-aera-gray">
                              สูงสุด ฿{formatAmount(maxAmt)}
                            </p>
                          ) : null}
                        </div>
                      </div>
                    </li>
                  );
                })}
              </ul>
            </TooltipProvider>
          </div>
        )}
      </div>

      <div className="shrink-0 space-y-3 px-4 pt-3 pb-[max(0.75rem,env(safe-area-inset-bottom))]">
        {step === "confirm" ? (
          <>
            {slipMatchSummary}
            <AeraButton
              type="button"
              variant="primary"
              fontWeight="medium"
              fullWidth
              className="cursor-pointer rounded-full!"
              loading={isSubmitting}
              disabled={
                isSubmitting ||
                selectedTotal <= 0 ||
                Boolean(allocationValidationError)
              }
              onClick={() => void handleConfirm()}
            >
              {isSubmitting
                ? "กำลังดำเนินการ…"
                : `ยืนยัน (${selectedEntries.length} ${counterpartUnit})`}
            </AeraButton>
            <AeraButton
              type="button"
              variant="outline"
              fontWeight="medium"
              fullWidth
              className="cursor-pointer rounded-full!"
              disabled={isSubmitting}
              onClick={() => {
                setStep("pick");
                setError(null);
              }}
            >
              ย้อนกลับ
            </AeraButton>
          </>
        ) : (
          <>
            {slipMatchSummary}
            <AeraButton
              type="button"
              variant="primary"
              fontWeight="medium"
              fullWidth
              className="cursor-pointer rounded-full!"
              disabled={selectedEntries.length === 0}
              onClick={() => {
                if (selectedEntries.length === 0) return;
                goToConfirmStep();
              }}
            >
              ถัดไป ({selectedEntries.length} {counterpartUnit})
            </AeraButton>
            <AeraButton
              type="button"
              variant="outline"
              fontWeight="medium"
              fullWidth
              className="cursor-pointer rounded-full!"
              onClick={close}
            >
              ปิด
            </AeraButton>
          </>
        )}
      </div>
    </ModalShell>
  );
}
